import {useEffect, useState} from 'react'
import {BuyerTaxFile} from '../../apps/customer-management/customers-list/customer-edit-modal/buyerTaxFile'
import {inquireTaxpayer} from './inquiryApi'

// The organisation's tax-file inquiry gives a file nothing but its «نام تجاری», which is often empty (TSP-API «استعلام
// وضعیت کارپوشه»). The name of a file without one comes from the free taxpayer inquiry of its economic code.

/** How many names are asked at a time: one person may hold many files, and each is a call to the organisation. */
const NAMES_AT_ONCE = 3

/** One inquiry per economic code while the page is open; one that failed may be asked again. */
const asked = new Map<string, Promise<string | null>>()

const inquireName = async (economicCode: string): Promise<string | null> => {
  try {
    const answer = await inquireTaxpayer(economicCode, false)
    // The answer may be another file of the same national code when this one has no inquiry on record.
    return answer?.ecd === economicCode ? answer.nm?.trim() || null : null
  } catch {
    asked.delete(economicCode)
    return null
  }
}

const askName = (economicCode: string): Promise<string | null> => {
  const known = asked.get(economicCode)
  if (known) return known
  const asking = inquireName(economicCode)
  asked.set(economicCode, asking)
  return asking
}

/**
 * The names of the files without a «نام تجاری», by economic code: not there yet while it is being asked, null when
 * the inquiry gave none.
 */
export const useTaxFileNames = (files: BuyerTaxFile[]): Record<string, string | null> => {
  const [names, setNames] = useState<Record<string, string | null>>({})
  const wanted = Array.from(
    new Set(
      files
        .filter((file) => !file.brand?.trim() && file.economicCode)
        .map((file) => file.economicCode as string)
    )
  ).join(',')

  useEffect(() => {
    if (!wanted) return
    let stopped = false
    const queue = wanted.split(',')
    const askNext = async (): Promise<void> => {
      const economicCode = queue.shift()
      if (!economicCode || stopped) return
      const name = await askName(economicCode)
      if (!stopped) setNames((current) => ({...current, [economicCode]: name}))
      return askNext()
    }
    for (let at = 0; at < NAMES_AT_ONCE; at++) askNext()
    return () => {
      stopped = true
    }
  }, [wanted])

  return names
}
